import React, { useEffect, useState } from "react";
import { Loader2, StickyNote, Trash2, Send } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { fetchNotes, createNote, deleteNote } from "@/lib/api";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function NotesDialog({ target, onClose, onCountChange }) {
  const open = !!target;
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!target) return;
    setNotes([]);
    setBody("");
    setLoading(true);
    fetchNotes(target.type, target.id)
      .then((n) => {
        setNotes(n);
        onCountChange?.(target, n.length);
      })
      .catch(() => toast.error("Couldn't load notes."))
      .finally(() => setLoading(false));
  }, [target]);

  const add = async () => {
    if (!body.trim()) return;
    setSaving(true);
    try {
      const note = await createNote(target.type, target.id, body.trim());
      const next = [note, ...notes];
      setNotes(next);
      onCountChange?.(target, next.length);
      setBody("");
    } catch {
      toast.error("Couldn't save note.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    const prev = notes;
    const next = notes.filter((x) => x.id !== id);
    setNotes(next);
    onCountChange?.(target, next.length);
    try {
      await deleteNote(id);
    } catch {
      setNotes(prev);
      onCountChange?.(target, prev.length);
      toast.error("Couldn't delete note.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="notes-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-forest flex items-center gap-2">
            <StickyNote className="h-5 w-5 text-terracotta" /> Notes
          </DialogTitle>
          <DialogDescription>{target?.label}</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Add a note… (e.g. Called, left voicemail)"
            className="rounded-xl border-forest/20 min-h-[64px]"
            data-testid="note-input"
            onKeyDown={(e) => {
              if ((e.metaKey || e.ctrlKey) && e.key === "Enter") add();
            }}
          />
          <Button
            onClick={add}
            disabled={saving || !body.trim()}
            data-testid="note-add"
            className="rounded-xl bg-forest hover:bg-forest-700 text-cream h-auto px-4 self-stretch"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>

        <div className="mt-2 max-h-[46vh] overflow-y-auto space-y-2 pr-1">
          {loading ? (
            <div className="py-8 grid place-items-center text-forest/40">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : notes.length === 0 ? (
            <p className="py-8 text-center text-sm text-forest/45" data-testid="notes-empty">
              No notes yet. Add the first one above.
            </p>
          ) : (
            notes.map((n) => (
              <div key={n.id} className="group rounded-xl border border-forest/10 bg-sand/50 p-3" data-testid="note-item">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm text-forest/85 whitespace-pre-wrap">{n.body}</p>
                  <button
                    onClick={() => remove(n.id)}
                    data-testid="note-delete"
                    className="opacity-0 group-hover:opacity-100 transition-opacity text-forest/30 hover:text-destructive shrink-0"
                    aria-label="Delete note"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <p className="mt-1.5 text-xs text-forest/40">{n.author} · {fmt(n.created_at)}</p>
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
